import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Search, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import PhotoUpload from './PhotoUpload';
import { toast } from 'sonner';

const PhotoManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCompletedBookings();
  }, []);

  const fetchCompletedBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Filter for completed bookings
        const completed = data.filter(b => b.status === 'completed' || b.status === 'in-progress');
        setBookings(completed);
        
        // Fetch photos for each booking
        for (const booking of completed) {
          fetchBookingPhotos(booking.id);
        }
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingPhotos = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/photos/booking/${bookingId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPhotos(prev => ({
          ...prev,
          [bookingId]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const filteredBookings = bookings.filter(booking =>
    booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.service.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (selectedBooking) {
    const bookingPhotos = photos[selectedBooking.id] || { before_photos: [], after_photos: [] };
    
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedBooking(null)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Back to All Bookings
        </button>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl">{selectedBooking.name}</CardTitle>
                <CardDescription>
                  {selectedBooking.service} - {selectedBooking.vehicleType}
                </CardDescription>
              </div>
              <Badge>{selectedBooking.status}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Before Photos */}
            <div>
              <PhotoUpload
                bookingId={selectedBooking.id}
                category="before"
                onUploadComplete={() => fetchBookingPhotos(selectedBooking.id)}
              />
              {bookingPhotos.before_photos.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {bookingPhotos.before_photos.map((photo) => (
                    <img
                      key={photo.id}
                      src={photo.cloudinary_url}
                      alt="Before"
                      className="w-full h-40 object-cover rounded-lg border border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(photo.cloudinary_url, '_blank')}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* After Photos */}
            <div>
              <PhotoUpload
                bookingId={selectedBooking.id}
                category="after"
                onUploadComplete={() => fetchBookingPhotos(selectedBooking.id)}
              />
              {bookingPhotos.after_photos.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {bookingPhotos.after_photos.map((photo) => (
                    <img
                      key={photo.id}
                      src={photo.cloudinary_url}
                      alt="After"
                      className="w-full h-40 object-cover rounded-lg border border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(photo.cloudinary_url, '_blank')}
                    />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Photo Management</CardTitle>
              <CardDescription>Upload before/after photos for completed jobs</CardDescription>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredBookings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">
                No completed bookings found. Complete a booking to upload photos.
              </p>
            ) : (
              filteredBookings.map((booking) => {
                const bookingPhotos = photos[booking.id] || { before_photos: [], after_photos: [] };
                const totalPhotos = bookingPhotos.before_photos.length + bookingPhotos.after_photos.length;

                return (
                  <div
                    key={booking.id}
                    className="border border-zinc-700 rounded-lg p-4 hover:bg-zinc-900 transition-colors cursor-pointer"
                    onClick={() => setSelectedBooking(booking)}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 bg-red-600/20 border border-red-500/50 rounded-full flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="w-6 h-6 text-red-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">{booking.name}</p>
                          <p className="text-sm text-gray-400">{booking.service} - {booking.vehicleType}</p>
                          <div className="flex items-center space-x-2 text-sm text-gray-400 mt-1">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(booking.preferred_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-center">
                          <p className="text-sm text-gray-400">Photos</p>
                          <p className="text-lg font-bold">
                            {totalPhotos > 0 ? totalPhotos : 'None'}
                          </p>
                        </div>
                        <Badge className={totalPhotos > 0 ? 'bg-green-600' : 'bg-gray-600'}>
                          {totalPhotos > 0 ? 'Has Photos' : 'No Photos'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhotoManagement;
